import React, { FC, useEffect, useLayoutEffect, useState } from "react"
import Loading from "../../../ui/Loading"
import { KTitleSpan, KDropdown, KButton, KSpan, KInput } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { handleAuthorizedRequest, users } from "../../../../apis"
import { User, UserLog, UserTakedown } from "../../../../types"
import { addCommasToNumber, checkAdminPermission, mapListToCamelCase, replaceCurrencies } from "../../../../utility"
import { toast } from "react-toastify"

import { connect } from "react-redux"
import { RootState } from "../../../../store"
import { SetURLSearchParams } from "react-router-dom"

const columnWidths: any = {
  ID: "5%",
  CREATED: "10%",
  TYPE: "10%",
  RELEASE: "20%",
  USER: "10%",
  LOG: "45%"
}

type AdminUserLogProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
}

const AdminUserLog: FC<AdminUserLogProps> = (props) => {
  const [userOptions, setUserOptions] = useState<KSelectOption[]>([])
  const [searchUser, setSearchUser] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption>(
    props.params.has("log_user_id")
      ? { label: props.params.get("log_user_id"), value: props.params.get("log_user_id") }
      : undefined
  )
  const [upc, setUpc] = useState<string>(props.params.get("log_upc") || "")
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [userLogList, setUserLogList] = useState<UserLog[]>([])
  const [hasNext, setHasNext] = useState(false)

  const scrollPosRef = React.useRef(0)

  useLayoutEffect(() => {
    if (!loading && scrollPosRef.current > 0) {
      window.scrollTo({
        top: scrollPosRef.current,
        behavior: "auto"
      })
    }
  }, [userLogList])

  const fetchUserLogList = (offset: number) => {
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const releaseUpcParam = upc ? `&upc=${upc}` : ""
    const idParam = props.params.has("log_id") ? `&id=${props.params.get("log_id")}` : ""

    setLoading(true)
    users(`/user_log/?offset=${offset}&limit=${limit}${userIdParam}${releaseUpcParam}${idParam}`, "GET").then(
      (resp) => {
        if (offset === 0) {
          setUserLogList(mapListToCamelCase(resp.data.results))
        } else {
          setUserLogList([...userLogList, ...mapListToCamelCase(resp.data.results)])
        }
        setHasNext(resp.data.results.length === limit)
        setLoading(false)
      }
    )
  }

  useEffect(() => {
    fetchUserLogList(0)
  }, [])

  const onSearchTextChanged = async (text: string) => {
    if (!text) return
    setUserOptions([])
    clearTimeout(timeoutId)
    setTimeoutId(
      setTimeout(async () => {
        await handleAuthorizedRequest(async () => {
          const response = await users(`/?limit=10&offset=0&username=${text}`, "get")
          setUserOptions(
            response.data.results.map((user: User) => ({
              value: user.id,
              label: user.username
            }))
          )
        }, "")
      }, 500)
    )
  }

  const renderUserLogs = () => {
    return (
      <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
        <div className="flex pb-2 gap-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => {
            return (
              <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
                <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
              </div>
            )
          })}
        </div>
        {userLogList.map((userLog) => {
          return (
            <div
              key={`user_log_${userLog.id}`}
              data-testid="admin-user-log-row"
              style={{ width: "100%" }}
              className="pt-5"
            >
              <div className="flex pt-2 gap-2" style={{ width: "100%", borderTop: "1px dashed grey" }}>
                <div style={{ width: columnWidths.ID }} className="flex gap-1 items-center">
                  <KSpan text={userLog.id?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.CREATED }} className="flex gap-1 items-center">
                  <KSpan text={userLog.created?.substring(0, 19)?.replace("T", " ") || "-"} />
                </div>
                <div style={{ width: columnWidths.TYPE }} className="flex gap-1 items-center">
                  <KSpan text={userLog.type} />
                </div>
                <div style={{ width: columnWidths.RELEASE }} className="flex gap-1 items-center">
                  <div className="flex flex-col gap-1">
                    <KSpan text={userLog.release?.title || "-"} />
                    <KSpan text={userLog.release?.upc || "-"} />
                  </div>
                </div>
                <div style={{ width: columnWidths.USER }} className="flex gap-1 items-center">
                  <KSpan text={`${userLog.user?.firstName || "-"} ${userLog.user?.lastName || "-"}`} />
                </div>
                <div style={{ width: columnWidths.LOG }} className="flex flex-col gap-1">
                  {userLog.log.split("\n").map((line, index) => (
                    <div key={`log_line_${index}`}>
                      <KSpan text={line} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const applyFilters = () => {
    if (props.params.has("log_id")) {
      props.params.delete("log_id")
    }
    setOffset(0)
    fetchUserLogList(0)
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTitleSpan fontSize={20} lineHeight="28px" text={"User Logs"} />
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <KSpan text="UPC" />
            <div>
              <KInput value={upc} onChange={(value) => setUpc(value)} placeholder="Release UPC" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <KSpan text="User" />
            {searchUser ? (
              <div
                className="relative"
                style={{ width: 250 }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setSearchUser(false)
                  }
                }}
              >
                <KDropdown
                  placeholder={"Search user to filter"}
                  width={240}
                  isClearable
                  selected={selectedUser}
                  onInputChange={onSearchTextChanged}
                  options={userOptions}
                  onSelect={(selectedOption) => {
                    setSelectedUser(selectedOption as KSelectOption)
                  }}
                />
              </div>
            ) : (
              <div className="">
                <KButton
                  leftIcon="/reports_icons/user.svg"
                  gap="4px"
                  text="Search user"
                  height="36px"
                  width="auto"
                  background="#F7F7F7"
                  hoverBackground="#F3F3F3"
                  activeBackground="#F7F7F7"
                  padding="8px"
                  shadowDisabled
                  onClick={() => {
                    setSearchUser(true)
                  }}
                />
              </div>
            )}
          </div>
          <div>
            <KButton
              height="35px"
              text="Apply Filters"
              background="black"
              textColor="white"
              onClick={() => applyFilters()}
            />
          </div>
        </div>
      </div>
      {renderUserLogs()}
      {hasNext && (
        <div className="flex justify-center pb-6 ">
          <KButton
            width="150px"
            background="transparent"
            onClick={() => {
              scrollPosRef.current = window.scrollY
              setOffset(offset + limit)
              fetchUserLogList(offset + limit)
            }}
            text={"Load More"}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(AdminUserLog)
